<template>
    <v-menu v-model="menu" offset-y :close-on-content-click="false" transition="slide-y-transition" content-class="notifications-menu">
        <template v-slot:activator="{ on, attrs }">
            <v-btn icon class="notification-button" v-bind="attrs" v-on="on">
                <v-badge :content="unreadCount" :value="unreadCount > 0" color="red" overlap>
                    <v-icon color="white">
                        mdi-bell
                    </v-icon>
                </v-badge>
            </v-btn>
        </template>

        <v-card class="notifications-card">
            <div class="notifications-header">
                <span class="notifications-title">
                    {{ $t('Уведомления') }}
                </span>
            </div>

            <v-divider />

            <div v-if="loading" class="notifications-loading">
                <v-progress-circular indeterminate color="primary" size="32" />
            </div>

            <div v-else-if="notifications.length === 0" class="notifications-empty">
                {{ $t('Нет уведомлений') }}
            </div>

            <v-list v-else class="notifications-list" two-line>
                <template v-for="notification in notifications">
                    <v-list-item :key="notification.record_id" class="notification-item" @click="openNotification(notification)">
                        <div class="notification-status" :class="getStatusClass(notification.code_name)" />

                        <v-list-item-content>
                            <v-list-item-title class="notification-item-title">
                                {{ getNotificationTitle(notification) }}
                            </v-list-item-title>

                            <v-list-item-subtitle class="notification-item-text">
                                <span>{{ $t('№') }}</span>

                                <a :href="getNotificationLink(notification)" class="notification-link" @click.stop.prevent="openNotificationLink(notification)">
                                    {{ notification.message_num }}
                                </a>

                                <span>
                                    {{ formatNotificationDate(notification.notification_date) }}
                                </span>
                            </v-list-item-subtitle>
                        </v-list-item-content>

                        <div class="notification-meta">
                            <div v-if="!notification.notification_read" class="notification-read-status unread" />
                        </div>
                    </v-list-item>

                    <v-divider :key="`divider-${notification.record_id}`" />
                </template>
            </v-list>
            <div v-if="totalItems > 0" class="notifications-pagination">
                <div class="notifications-page-size">
                    <span class="notifications-page-size-label"> {{ $t('Строк на странице') }}: </span>

                    <div class="notifications-page-size-control">
                        <select :value="itemsPerPage" class="notifications-page-size-select" @change="changeItemsPerPage($event.target.value)">
                            <option v-for="option in itemsPerPageOptions" :key="option" :value="option">
                                {{ option }}
                            </option>
                        </select>

                        <v-icon small class="notifications-page-size-icon">
                            mdi-menu-down
                        </v-icon>
                    </div>
                </div>

                <div class="notifications-page-info">{{ pageStart }}–{{ pageEnd }} из {{ totalItems }}</div>

                <div class="notifications-page-actions">
                    <v-btn icon small :disabled="page <= 1" @click="changePage(page - 1)">
                        <v-icon size="20">
                            mdi-chevron-left
                        </v-icon>
                    </v-btn>

                    <v-btn icon small :disabled="page >= pageCount" @click="changePage(page + 1)">
                        <v-icon size="20">
                            mdi-chevron-right
                        </v-icon>
                    </v-btn>
                </div>
            </div>
        </v-card>
    </v-menu>
</template>

<script>
import PrescriptionOZPService from '@/services/apiRequests/prescription_ozp.service';
export default {
    name: 'NotificationsMenu',

    data() {
        return {
            menu: false,
            loading: false,
            notifications: [],

            page: 1,
            itemsPerPage: 10,
            itemsPerPageOptions: [10, 20, 50],

            totalItems: 0,
            totalUnreadCount: 0,

            notificationsTimer: null
        };
    },

    created() {
        this.loadNotifications();
        this.loadUnreadCount();

        this.notificationsTimer = setInterval(() => {
            this.loadNotifications();
            this.loadUnreadCount();
        }, 30000);
    },

    beforeDestroy() {
        clearInterval(this.notificationsTimer);
    },

    watch: {
        menu(value) {
            if (value) {
                this.page = 1;

                this.loadNotifications();
                this.loadUnreadCount();
            }
        }
    },

    computed: {
        unreadCount() {
            return this.totalUnreadCount;
        },

        pageCount() {
            return Math.max(Math.ceil(this.totalItems / this.itemsPerPage), 1);
        },

        pageStart() {
            if (this.totalItems === 0) {
                return 0;
            }

            return (this.page - 1) * this.itemsPerPage + 1;
        },

        pageEnd() {
            return Math.min(this.page * this.itemsPerPage, this.totalItems);
        }
    },

    methods: {
        async loadUnreadCount() {
            try {
                this.totalUnreadCount = await PrescriptionOZPService.getUnreadNotificationsCount();
            } catch (error) {
                console.error('Ошибка получения общего количества непрочитанных уведомлений:', error);

                this.totalUnreadCount = 0;
            }
        },
        async openNotificationLink(notification) {
            const url = this.getNotificationLink(notification);

            try {
                if (!notification.notification_read) {
                    await PrescriptionOZPService.markNotificationAsRead(notification.record_id);

                    this.$set(notification, 'notification_read', true);

                    this.totalUnreadCount = Math.max(this.totalUnreadCount - 1, 0);
                }

                window.location.href = url;
            } catch (error) {
                console.error('Ошибка отметки уведомления как прочитанного:', error);
            }
        },
        getNotificationLink(notification) {
            const origin = window.location.origin;

            if (notification.code_name.startsWith('prescription') || notification.code_name.startsWith('event')) {
                return `${origin}/prescription-ozp/${notification.message_rid}`;
            }

            if (notification.code_name.startsWith('request')) {
                return `${origin}/request-ozp/${notification.message_rid}`;
            }

            return origin;
        },
        async loadNotifications() {
            this.loading = true;

            try {
                const offset = (this.page - 1) * this.itemsPerPage;

                const data = await PrescriptionOZPService.getNotifications({
                    limit: this.itemsPerPage,
                    offset
                });

                this.notifications = Array.isArray(data.ahs_requests) ? data.ahs_requests : [];

                this.totalItems = Number(data.pagination?.row_count) || 0;

                if (this.page > this.pageCount) {
                    this.page = this.pageCount;
                    await this.loadNotifications();
                }
            } catch (error) {
                console.error('Ошибка загрузки уведомлений:', error);

                this.notifications = [];
                this.totalItems = 0;
            } finally {
                this.loading = false;
            }
        },
        async changePage(page) {
            if (page === this.page) {
                return;
            }

            this.page = page;
            await this.loadNotifications();
        },

        async changeItemsPerPage(value) {
            this.itemsPerPage = Number(value);
            this.page = 1;

            await this.loadNotifications();
        },
        getNotificationTitle(notification) {
            const locale = this.$i18n.locale;

            if (locale === 'kz') {
                return notification.notification_type_name___kz || notification.notification_type_name___ru || '';
            }

            return notification.notification_type_name___ru || notification.notification_type_name___kz || '';
        },

        getStatusClass(codeName) {
            const classes = {
                prescription_returned: 'status-returned',
                prescription_completed: 'status-completed',
                prescription_checked: 'status-completed',
                prescription_closed: 'status-closed',
                event_returned: 'status-returned',
                event_executed: 'status-completed',
                event_sended: 'status-checked',
                event_sign_act_file: 'status-completed',
                event_add_act_file: 'status-completed',
                event_add_file: 'status-completed',
                request_in_progress: 'status-checked',
                request_in_queue: 'status-checked',
                request_executed: 'status-completed',
                request_assigned: 'status-checked',
                request_canceled: 'status-canceled'
            };

            return classes[codeName] || 'status-default';
        },

        formatNotificationDate(value) {
            if (!value) {
                return '';
            }

            const date = new Date(value);

            return date.toLocaleString(this.$i18n.locale === 'kz' ? 'kk-KZ' : 'ru-RU', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        },

        markAllAsRead() {
            this.notifications = this.notifications.map(item => ({
                ...item,
                notification_read: true
            }));
        },

        openNotification(notification) {
            notification.notification_read = true;
            this.menu = false;

            if (!notification.message_rid) {
                return;
            }

            if (notification.code_name && notification.code_name.startsWith('prescription_')) {
                this.$router.push({
                    name: 'PrescriptionOZPDetail',
                    params: {
                        id: notification.message_rid
                    }
                });
            }
        }
    }
};
</script>

<style scoped>
.notifications-card {
    width: 570px;
    max-width: calc(100vw - 24px);
}

.notifications-header {
    min-height: 52px;
    padding: 8px 16px 8px 22px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.notifications-title {
    font-size: 16px;
    font-weight: 600;
    color: #1d2b4f;
}

.mark-all-button {
    font-size: 13px;
    text-transform: none;
    letter-spacing: normal;
}

.notifications-list {
    padding: 0;
    overflow-y: auto;
    max-height: calc(100vh - 250px);
}

.notification-item {
    min-height: 80px;
    padding: 10px 18px 10px 22px;
}

.notification-item:hover {
    background-color: #f7f9fe;
}

.notification-status {
    width: 17px;
    height: 17px;
    min-width: 17px;
    margin-right: 18px;
    border-radius: 50%;
}

.status-returned {
    background-color: #f5a000;
}

.status-canceled {
    background-color: #e53935;
}

.status-completed {
    background-color: #35b65a;
}

.status-checked {
    background-color: #2f91f3;
}

.status-closed {
    background-color: #adb5bd;
}

.status-default {
    background-color: #8e44ad;
}

.notification-item-title {
    margin-bottom: 3px;
    white-space: normal;
    overflow: visible;
    font-size: 14px;
    line-height: 19px;
    font-weight: 600;
    color: #172343;
}

.notification-item-text {
    white-space: normal;
    overflow: visible;
    font-size: 13px;
    line-height: 18px;
    color: #35405f;
}

.notification-meta {
    min-width: 125px;
    margin-left: 15px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
}

.notification-date {
    font-size: 12px;
    color: #8b92a4;
    white-space: nowrap;
}

.notification-read-status {
    width: 9px;
    height: 9px;
    margin-left: 16px;
    border-radius: 50%;
    background-color: #b7bdc9;
}

.notification-read-status.unread {
    background-color: #2f91f3;
}

.notifications-footer {
    height: 54px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.show-all-button {
    text-transform: none;
    letter-spacing: normal;
    font-weight: 500;
}

.notification-link {
    color: #1976d2;
    text-decoration: none;
    font-weight: 500;
    margin: 0 4px;
}

.notification-link:hover {
    text-decoration: underline;
}

.notifications-loading,
.notifications-empty {
    min-height: 160px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #7b8294;
}

.notification-button {
    margin: 0 5px;
}
.notifications-pagination {
    width: 100%;
    height: 52px;
    padding: 0 18px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: 24px;
    border-top: 1px solid #e0e0e0;
    background: #ffffff;
    box-sizing: border-box;
    white-space: nowrap;
}

.notifications-page-size {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 10px;
}

.notifications-page-size-label {
    font-size: 12px;
    line-height: 20px;
    color: rgba(0, 0, 0, 0.72);
    white-space: nowrap;
}

.notifications-page-size-control {
    position: relative;
    width: 48px;
    height: 32px;
    display: inline-flex;
    flex: 0 0 48px;
    align-items: center;
}

.notifications-page-size-select {
    width: 48px;
    height: 32px;
    padding: 0 20px 0 4px;
    border: 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.42);
    border-radius: 0;
    outline: none;
    background: transparent;
    color: rgba(0, 0, 0, 0.87);
    font-family: inherit;
    font-size: 12px;
    line-height: 32px;
    cursor: pointer;

    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
}

.notifications-page-size-select:focus {
    border-bottom-color: #1976d2;
}

.notifications-page-size-icon {
    position: absolute;
    right: 0;
    pointer-events: none;
    color: rgba(0, 0, 0, 0.54);
}

.notifications-page-info {
    min-width: 90px;
    flex: 0 0 auto;
    font-size: 12px;
    line-height: 20px;
    color: rgba(0, 0, 0, 0.72);
    text-align: center;
    white-space: nowrap;
}

.notifications-page-actions {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 14px;
}

.notifications-page-actions .v-btn {
    width: 28px !important;
    height: 28px !important;
    margin: 0;
}

.notifications-page-actions .v-btn::before {
    background-color: transparent;
}
@media (max-width: 600px) {
    .notifications-card {
        width: calc(100vw - 16px);
    }

    .notifications-header {
        align-items: flex-start;
    }

    .notification-item {
        padding-left: 12px;
        padding-right: 12px;
    }

    .notification-meta {
        min-width: auto;
        margin-left: 8px;
    }

    .notification-read-status {
        margin-left: 8px;
    }

    .notifications-pagination {
        height: 52px;
        padding: 0 10px;
        justify-content: space-between;
        gap: 8px;
    }

    .notifications-page-size {
        gap: 5px;
    }

    .notifications-page-size-label,
    .notifications-page-info {
        font-size: 11px;
    }

    .notifications-page-info {
        min-width: auto;
    }

    .notifications-page-actions {
        gap: 2px;
    }
}
</style>
